<template>
  <div :class="['ChainedBanner', {'inverted': inverted}]" :style="{'background-color': bgcolor, 'color': color}">
    <div :class="['bar', {showed}]" :style="{'width': `${width}%`, 'transform': `translateX(${getTranslate})`}" ref="$bar">
      <div class="content">
        <div class="value">
          <slot name="value"></slot>
        </div>
        <div class="description">
          <slot name="description"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import ViewportMixins from '@mixins/viewport_mixins';

  export default {
    name: 'ChainedBanner',
    mixins: [ViewportMixins],
    props: {
      inverted: {
        type: Boolean,
        default: false
      },
      width: {
        type: Number,
        default: 80
      },
      bgcolor: {
        type: String,
        default: null
      },
      color: {
        type: String,
        default: null
      }
    },
    data() {
      return {
        visible: false,
        showed: false
      };
    },
    computed: {
      getTranslate() {
        if (!this.visible) {
          return this.inverted ? '100%' : '-100%';
        }

        return 0;
      }
    },
    mounted() {
      setTimeout(() => {
        window.addEventListener('scroll', this.isVisible);
        this.isVisible();
      }, 500);
    },
    methods: {
      isVisible() {
        if (!this.visible && this.isElementXPercentInViewport(this.$el, 66)) {
          this.visible = true;
          window.removeEventListener('scroll', this.isVisible);
          const afterShow = () => {
            this.showed = true;
            this.$refs.$bar.removeEventListener('transitionend', afterShow);
          };
          this.$refs.$bar.addEventListener('transitionend', afterShow);
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import '~$scss/variables';
  @import '~$scss/utility';

  .ChainedBanner + .ChainedBanner {
    padding-top: 0 !important;
  }

  .ChainedBanner {
    display: flex;
    padding-left: 0;
    padding-right: 0;
    width: 100%;
    background: transparent;
    @include responsive-property(padding-top, $section-padding);
    @include responsive-property(padding-bottom, $section-padding);

    // Theme: double-padding-top to be used when previous block has colored bg
    &.double-padding-top {
      @include responsive-property(padding-top, $section-double-padding);
    }

    // Theme: double-padding-bottom to be used when next block has colored bg
    &.double-padding-bottom {
      @include responsive-property(padding-bottom, $section-double-padding);
    }

    // Theme: half-padding-top
    &.half-padding-top {
      @include responsive-property(padding-top, $section-half-padding);
    }

    // Theme: half-padding-bottom
    &.half-padding-bottom {
      @include responsive-property(padding-bottom, $section-half-padding);
    }

    // Theme: last-section-padding-bottom
    &.last-section-padding-bottom {
      @include responsive-property(padding-bottom, $last-section-padding);
    }

    // Theme: first-section-padding-top
    &.first-section-padding-top {
      @include responsive-property(padding-top, $last-section-padding);
    }

    // Theme: regular-padding-top
    &.regular-padding-top {
      @include responsive-property(padding-top, $section-regular-padding);
    }

    // Theme: regular-padding-bottom
    &.regular-padding-bottom {
      @include responsive-property(padding-bottom, $section-regular-padding);
    }

    .bar {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: $color-blue;
      color: $color-white;
      padding: 50px 0;
      transition: transform 1s ease;

      @media (min-width: $bootstrap-lg) {
        min-height: 370px;
      }

      &.showed {
        .content {
          opacity: 1;
        }
      }

      .content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 1s ease;

        @media (min-width: $bootstrap-md) {
          flex-direction: row;
        }

        .value {
          flex: 1 1 50%;
          text-align: right;
          padding: 0 25px;

          *, ::v-deep * {
            text-align: inherit !important;
          }
        }

        .description {
          flex: 1 1 50%;
          text-align: left;
          padding: 0 25px;

          *, ::v-deep * {
            text-align: inherit !important;
          }
        }

        *, ::v-deep * {
          color: inherit !important;
        }
      }
    }

    &.inverted {
      justify-content: flex-end;

      .bar {
        background-color: $color-light-green;
        color: $color-blue;
      }
    }
  }
</style>
